import { useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { acqParams } from "@/lib/acquisition";

// Value-first, opt-in email capture. Never blocks the free result. Marketing consent required.
export function LeadCapture({ tool = "", compact = false }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!email || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (!consent) { setError("Please check the box to receive your results and free resources."); return; }
    setLoading(true);
    try {
      await api.post("/public/lead", {
        email, first_name: firstName, consent, tool,
        company_website: website,
        ...acqParams({ landing: window.location.pathname }),
      });
      setDone(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object" && !Array.isArray(detail)) msg = detail.message || detail.msg;
      setError(msg || formatApiError(detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/10 p-5 flex items-start gap-3" data-testid="lead-capture-success">
        <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 shrink-0" />
        <div>
          <p className="text-white font-medium">Check your inbox!</p>
          <p className="text-sm text-[#A1A1AA]">We're sending your results and free business resources. You can unsubscribe anytime.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-white/10 bg-[#121214] ${compact ? "p-5" : "p-6"}`} data-testid="lead-capture">
      <div className="flex items-center gap-2 mb-1.5">
        <Mail className="w-4 h-4 text-[#D4AF37]" />
        <h4 className="font-heading text-lg text-white">Get your results & free business resources by email</h4>
      </div>
      <p className="text-sm text-[#A1A1AA] mb-4">We'll email your results plus a founder's starter kit. No spam, unsubscribe anytime.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="lead-first-name" placeholder="First name (optional)" value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525B]" />
        <Input data-testid="lead-email" type="email" placeholder="you@example.com" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525B]" />
      </div>
      {/* Honeypot: hidden from users, catches bots */}
      <input type="text" tabIndex={-1} autoComplete="off" value={website}
        onChange={(e) => setWebsite(e.target.value)}
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} aria-hidden="true" />
      <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="lead-consent-label">
        <input type="checkbox" data-testid="lead-consent" checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 accent-[#D4AF37]" />
        <span className="text-xs text-[#A1A1AA]">Yes, email me my results and free business resources from Fairview Systems. I can unsubscribe anytime.</span>
      </label>
      {error && <p className="text-sm text-[#EF4444] mt-2" data-testid="lead-error">{error}</p>}
      <Button onClick={submit} disabled={loading} data-testid="lead-submit"
        className="mt-4 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-6 h-10">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Email me my results"}
      </Button>
    </div>
  );
}
